import { useRef, useState } from 'react';
import styled from 'styled-components';
import { Search } from 'lucide-react';
import { colors, text, ui } from '@annote/core/styles';

export type TextInputProps = {
  id: string;
  disabled?: boolean;
  label?: string;
  placeholder?: string;
  iconOption?: string;
  /** When iconOption is set, size of the icon in px. Defaults to ui.getIconSize('input'). */
  iconSize?: number;
  initialValue?: string;
  outsideValue?: string;
  className?: string;
  isFocusedByDefault?: boolean;
  onChange?: (id: string, value: string) => void | Promise<void>;
  onFocus?: () => void;
  onBlur?: () => void;
  isSubtle?: boolean;
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.label`
  color: ${colors.icon.primary};
  font-weight: ${text.weight.bold};
  font-family: ${text.family.primary};
  font-size: ${text.size.primary};
  line-height: ${text.height.primary};
`;

const Input = styled.input`
  appearance: none;
  border: none;
  outline: none;
  height: 100%;
  flex: 1;

  color: ${colors.text.primary};
  background: inherit;
  font-family: ${text.family.primary};
  font-size: ${text.size.small};
  line-height: ${text.height.primary};

  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'text')};

  &::placeholder {
    color: ${colors.text.header};
  }
`;

const InputGroup = styled.div<{ isFocused: boolean; disabled: boolean; $isSubtle: boolean }>`
  display: flex;
  align-items: center;
  gap: 5px;
  background: ${(props) => {
    if (props.$isSubtle) return 'transparent';
    if (props.disabled) return colors.bg.hover;
    return colors.bg.primary;
  }};
  outline: ${(props) => {
    if (props.$isSubtle) return '1px solid rgba(0, 0, 0, 0.08)';
    if (props.isFocused) return `1px solid ${colors.borders.input.selected}`;
    return `1px solid ${colors.borders.input.primary}`;
  }};
  ${(props) => (props.disabled ? `outline: 1px solid ${colors.bg.selected};` : '')}
  border-radius: 4px;
  box-shadow: ${(props) => (props.$isSubtle ? 'none' : 'inset 1px 1px 4px rgba(0, 0, 0, 0.08)')};
  padding: 7px 14px;
  height: 100%;
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'text')};

  &:hover {
    ${(props) => (props.disabled ? '' : `outline: 1px solid ${colors.borders.input.selected};`)}
  }

  svg path {
    fill: ${colors.icon.placeholder};
  }
`;

const StyledIcon = styled(Search)<{ disabled: boolean }>`
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'text')};
  color: ${colors.icon.placeholder};
`;

const TextInput: React.FC<TextInputProps> = ({
  id,
  disabled = false,
  label,
  placeholder,
  iconOption,
  iconSize = ui.getIconSize('input'),
  initialValue = '',
  outsideValue,
  className,
  isFocusedByDefault = false,
  onChange,
  onFocus,
  onBlur: onBlurProp,
  isSubtle = false,
}) => {
  const [value, setValue] = useState<string>(initialValue);
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleChange: React.ChangeEventHandler<HTMLInputElement> = (event) => {
    const newValue = event.currentTarget.value;
    setValue(newValue);
    if (!onChange) return;
    onChange(id, newValue);
  };

  const handleInputGroupClick = (): void => {
    if (!inputRef.current || disabled) return;
    setIsFocused(true);
    inputRef.current.focus();
  };

  const handleFocus = (): void => {
    setIsFocused(true);
    if (onFocus) {
      onFocus();
    }
  };

  const handleBlur = (): void => {
    setIsFocused(false);
    if (onBlurProp) {
      onBlurProp();
    }
  };

  const isOutsideControl = typeof outsideValue !== 'undefined';
  const inputValue = isOutsideControl ? outsideValue : value;

  return (
    <Wrapper className={className}>
      {label && <Label>{label}</Label>}
      <InputGroup onClick={handleInputGroupClick} isFocused={isFocused} disabled={disabled} $isSubtle={isSubtle}>
        {iconOption === 'search' && <StyledIcon disabled={disabled} size={iconSize} />}
        <Input
          id={id}
          ref={inputRef}
          placeholder={placeholder}
          value={inputValue}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          disabled={disabled}
          autoFocus={isFocusedByDefault}
        />
      </InputGroup>
    </Wrapper>
  );
};

export default TextInput;
